import { HugeiconsIcon } from "@hugeicons/react";
import {
  CallEnd01Icon,
  Cancel01Icon,
  DialpadCircle01Icon,
  Mic01Icon,
  MicOff01Icon,
  SquareArrowExpand01Icon,
  User02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import {
  isCallerUnknown,
  UNKNOWN_CALLER_LABEL
} from "shared/utils/transferred-call";
import { formatPhoneDisplay } from "../views/phone-numbers/lib/format";
import { useSoftphone } from "./SoftphoneProvider";
import { describeCall, type CallDisplay } from "./callDisplay";
import { SOFTPHONE_OVERLAY_Z_CLASS } from "./stacking";
import { useDraggableOverlay, type DragOffset } from "./useDraggableOverlay";

const ACTIVE_AVATAR_COLOR = "#29B6F6";
const ENDED_AVATAR_COLOR = "#9CA3AF";

interface ActiveCallPillProps {
  // Live in-call duration, ticked once a second by the overlay.
  elapsedSeconds: number;
  // Expand the pill back into the full call card.
  onExpand: () => void;
  // Expand the card with the dial pad already open (CRM-1190).
  onOpenKeypad: () => void;
  // Drag position, owned by the overlay so it survives minimize/expand.
  dragOffset: DragOffset;
  onDragOffsetChange: (offset: DragOffset) => void;
}

/**
 * Minimized in-call pill (CRM-811, Figma 22148:162504 — the collapsed bar above
 * each card). Shows the destination + live status; hang-up while a call is live,
 * dismiss once it has ended. Clicking the bar re-opens the full card, dragging
 * it moves it off whatever it covers (CRM-1112). On an answered call it also
 * carries the dial-pad shortcut, which expands the card onto the pad (CRM-1190).
 */
export function ActiveCallPill({
  elapsedSeconds,
  onExpand,
  onOpenKeypad,
  dragOffset,
  onDragOffsetChange
}: ActiveCallPillProps) {
  const {
    activeCall,
    status,
    callDurationSeconds,
    hangup,
    dismissCall,
    isMuted,
    toggleMute
  } = useSoftphone();

  const { nodeRef, dragClassName, style, dragProps } = useDraggableOverlay({
    offset: dragOffset,
    onOffsetChange: onDragOffsetChange
  });

  if (!activeCall) return null;

  const view = describeCall(status, { elapsedSeconds, callDurationSeconds });
  const displayName = activeCall.toLabel?.trim();
  // Same rule as the expanded card: an escalated call the api could not
  // attribute carries the AI agent line's own number (CRM-1393).
  const callerUnknown =
    activeCall.direction === "inbound" &&
    isCallerUnknown(activeCall.toNumber, activeCall.transferredFromNumber);
  const displayNumber = callerUnknown
    ? UNKNOWN_CALLER_LABEL
    : formatPhoneDisplay(activeCall.toNumber);

  return (
    <div
      ref={nodeRef}
      role="status"
      aria-live="polite"
      style={style}
      {...dragProps}
      className={cx(
        "fixed bottom-4 right-4 flex items-center gap-3 rounded-full bg-content-1000 py-2 pl-2 pr-3 text-white shadow-lg",
        dragClassName,
        SOFTPHONE_OVERLAY_Z_CLASS
      )}
    >
      <button
        type="button"
        onClick={onExpand}
        aria-label="Expand call"
        className="flex items-center gap-3 rounded-full pr-1 text-left"
      >
        <Avatar
          name={displayName}
          size={32}
          className="text-xs font-semibold"
          fallbackText={<HugeiconsIcon icon={User02Icon} size={16} />}
          fallbackBackgroundColor={
            view.isLive ? ACTIVE_AVATAR_COLOR : ENDED_AVATAR_COLOR
          }
        />
        <span className="flex flex-col leading-tight">
          <span className="text-sm font-medium">{displayNumber}</span>
          {displayName ? (
            <span className="text-xs text-white/60">{displayName}</span>
          ) : null}
        </span>
      </button>

      <span
        className={cx(
          "ml-1 text-sm tabular-nums",
          view.tone === "error" ? "text-danger-400" : "text-white/80"
        )}
      >
        {view.statusLine}
      </span>

      <PillActions
        view={view}
        isMuted={isMuted}
        onToggleMute={toggleMute}
        onOpenKeypad={onOpenKeypad}
        onHangup={hangup}
        onDismiss={dismissCall}
        onExpand={onExpand}
      />
    </div>
  );
}

interface PillActionsProps {
  view: CallDisplay;
  isMuted: boolean;
  onToggleMute: () => void;
  onOpenKeypad: () => void;
  onHangup: () => void;
  onDismiss: () => void;
  onExpand: () => void;
}

// Split out of the pill itself only to keep each piece readable: the bar is a
// row of per-status icon buttons, and inlining all of them made the component
// harder to follow (and tripped the cognitive-complexity lint).
function PillActions({
  view,
  isMuted,
  onToggleMute,
  onOpenKeypad,
  onHangup,
  onDismiss,
  onExpand
}: PillActionsProps) {
  return (
    <>
      {view.showMute ? (
        <button
          type="button"
          aria-label={isMuted ? "Unmute" : "Mute"}
          onClick={onToggleMute}
          className={cx(
            "grid size-8 place-items-center rounded-full transition",
            isMuted
              ? "bg-white text-content-1000 hover:bg-white/90"
              : "bg-white/10 text-white hover:bg-white/20"
          )}
        >
          <HugeiconsIcon icon={isMuted ? MicOff01Icon : Mic01Icon} size={16} />
        </button>
      ) : null}

      {view.showKeypad ? (
        <button
          type="button"
          aria-label="Open dial pad"
          onClick={onOpenKeypad}
          className="grid size-8 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
        >
          <HugeiconsIcon icon={DialpadCircle01Icon} size={16} />
        </button>
      ) : null}

      <button
        type="button"
        aria-label={view.showHangup ? "End call" : "Dismiss"}
        onClick={view.showHangup ? onHangup : onDismiss}
        className={cx(
          "grid size-8 place-items-center rounded-full transition",
          view.showHangup
            ? "bg-danger-500 text-white hover:bg-danger-600"
            : "bg-white/10 text-white hover:bg-white/20"
        )}
      >
        <HugeiconsIcon
          icon={view.showHangup ? CallEnd01Icon : Cancel01Icon}
          size={16}
        />
      </button>

      <button
        type="button"
        aria-label="Expand call"
        onClick={onExpand}
        className="grid size-8 place-items-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white"
      >
        <HugeiconsIcon icon={SquareArrowExpand01Icon} size={16} />
      </button>
    </>
  );
}
